import {
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useDisclosure,
  Wrap,
} from "@chakra-ui/react";
import { useCallback, useMemo } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import MultiButton from "../../../components/MultiButton";
import {
  POLICIES_POLICY_VALUE_DELETE,
  POLICIES_POLICY_VALUE_SOFT_DELETE,
} from "../../../constants/policies";
import { apiGetErrors } from "../../../helpers/api";
import useCanPerform from "../../../hooks/useCanPerform";
import useRequest from "../../../hooks/useRequest";
import generateApiUri from "../../../libraries/utils/generateApiUri";

/**
 * @typedef {object} Props
 * @property {import("../../../types/CustomerDocumentPage").CustomerDocumentPage} page
 * @property {() => void} onSuccess
 * @property {(props: {isAllowed: boolean, trigger: () => void}) => import("react").ReactElement | null} children
 *
 * @param {import("react").PropsWithoutRef<Props>} props
 */
function SessionsRegistrationsDatesActionDelete({ page, onSuccess, children }) {
  const { isOpen, onOpen, onClose } = useDisclosure();

  const intl = useIntl();

  const { request, toastSuccess, toastError } = useRequest();

  const canPerform = useCanPerform();

  const canSoftDelete = canPerform({
    policies: POLICIES_POLICY_VALUE_SOFT_DELETE,
    resourcePolicies: page.meta?.policies,
  });

  const canHardDelete = canPerform({
    policies: POLICIES_POLICY_VALUE_DELETE,
    resourcePolicies: page.meta?.policies,
  });

  const isAllowed = canSoftDelete || canHardDelete;

  const deleteResource = useCallback(
    ({ isHardDelete }) => {
      request(
        generateApiUri({
          id: "@customers.documents.pages.delete",
          parameters: {
            pageId: page.id,
          },
          query: {
            hard_delete: isHardDelete,
          },
        }),
        { method: "DELETE" },
      )
        .then((response) => {
          toastSuccess({
            status: "success",
            title: intl.formatMessage({
              defaultMessage: "Le fichier a bien été supprimé.",
            }),
          });
          onClose();
        })
        .catch((response) => {
          const { _detail } = apiGetErrors({
            error: response,
            detailFallback: intl.formatMessage({
              defaultMessage: "Aucun détail fourni.",
            }),
          });
          toastError({
            status: "error",
            title: "Une erreur est survenue lors de la suppression du fichier.",
            description: _detail,
            duration: null,
            isClosable: true,
          });
        })
        .finally(() => {
          onSuccess?.();
        });
    },
    [request, page.id, toastSuccess, intl, onClose, toastError, onSuccess],
  );

  const actions = useMemo(() => {
    const actions = [];
    if (canSoftDelete) {
      actions.push({
        name: intl.formatMessage({
          defaultMessage: "Supprimer",
        }),
        execute: () => {
          deleteResource({ isHardDelete: false });
        },
      });
    }
    if (canHardDelete) {
      actions.push({
        name: intl.formatMessage({
          defaultMessage: "Supprimer définitivement",
        }),
        execute: () => {
          deleteResource({ isHardDelete: true });
        },
      });
    }
    return actions;
  }, [canHardDelete, canSoftDelete, deleteResource, intl]);

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="xl" isCentered>
        <ModalOverlay />

        <ModalContent>
          <ModalHeader>
            <FormattedMessage defaultMessage="Suppression d'un fichier" />
          </ModalHeader>

          <ModalCloseButton />

          <ModalBody>
            <FormattedMessage
              defaultMessage="Êtes-vous sûr de vouloir supprimer le fichier{nbr}?"
              values={{
                nbr: "\u00A0",
              }}
            />
          </ModalBody>

          <ModalFooter>
            <Wrap align="flex-end" spacing=".5rem">
              <Button
                onClick={onClose}
                variant="outline"
                colorScheme="gray"
                size="sm">
                <FormattedMessage defaultMessage="Annuler" />
              </Button>

              <MultiButton
                actions={actions}
                buttonGroupProps={{ size: "sm" }}
              />
            </Wrap>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {children({
        isAllowed,
        trigger: onOpen,
      })}
    </>
  );
}

export default SessionsRegistrationsDatesActionDelete;
